/**
 * Frontend Decimal Registry
 *
 * Module-level token list for synchronous getDecimals() lookups.
 * Populated from FALLBACK_TOKENS on import, updated from API on app init.
 *
 * Imports from @/config/fallback-tokens (not @/services/api) to avoid
 * circular import: api.ts → decimals.ts → api.ts
 */

import { FALLBACK_TOKENS } from '@/config/fallback-tokens'

interface TokenEntry {
  chainName: string
  symbol: string
  decimals: number
}

let tokenList: TokenEntry[] = [...FALLBACK_TOKENS]

/**
 * Replace the token list (called from the API service layer when
 * dexApi.getTokens() returns fresh data on app init).
 */
export function setTokenList(tokens: TokenEntry[]): void {
  tokenList = tokens
}

/**
 * Look up decimal precision for a token on a specific chain.
 * Falls back to FALLBACK_TOKENS if the API hasn't responded yet.
 * Throws on unknown token — never defaults to 6.
 */
export function getDecimals(chainName: string, symbol: string): number {
  const token = tokenList.find(
    t => t.chainName === chainName && t.symbol === symbol
  )
  if (!token) {
    throw new Error(
      `getDecimals: unknown token (chain=${chainName}, symbol=${symbol}). ` +
      `Frontend token list has ${tokenList.length} entries.`
    )
  }
  return token.decimals
}
